import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import cx from 'classnames';
import { isEmpty, isNull } from 'lodash/lang';
import { maxBy } from 'lodash/math';
import { get } from 'lodash/object';
import moment from 'moment';

import LogRowStatusHistoryApi from '../../../../../api/patient/LogRowStatusHistoryApi';
import ModalBoxes from '../../../../../common/feedback/ModalBoxes/ModalBoxes';
import useSessionStorage from '../../../../../common/hooks/useSessionStorage';
import { UNEXPECTED } from '../../../../../constants/encounter';
import { TD_MANAGE_ANY_ENCOUNTER_DATA } from '../../../../../constants/userOperations';
import {
  ROLE_PATIENT_REPRESENTATIVE,
  ROLE_PRINCIPAL_INVESTIGATOR,
  ROLE_STUDY_MANAGER,
  ROLE_SYSTEM_ADMINISTRATOR
} from '../../../../../constants/userRoles';
import { userHasAccessAs, userHasAccessTo, userHasRole } from '../../../../../services/auth';
import { isNotNewReviewableLogType, isReviewableLogType } from '../../../../../services/itemGroupQuestionService';
import { resolveSmStatus } from '../../../../../services/logRowStatusService';
import { generateUrlByKey, useCurrentRoute } from '../../../../root/router';
import { ALLOW_READ, DENY_ALL } from '../../../setup/Protocol/ProtocolSetup/ProtocolGroupsSetup/permissionType';
import {
  getNotStartedEncounterNames,
  getStartedEncounterNames,
  isAbleToEditEncounter,
  notPerformedAndNotAskedShortcut,
  saveStudyManagerForEncounter
} from '../EncounterDescNew/encounterDetailsService';
import LogRowCloseReopen from '../EncounterDescNew/item-group/LogRowCloseReopen';
import ItemGroup from '../EncounterDescNew/ItemGroup';
import NotPerformShortcutModal from '../EncounterDescNew/NotPerformShortcutModal';
import ItemGroupBar from '../ItemGroupBar/ItemGroupBar';
import { cancelSitePatientEncounter, reopenSitePatientEncounter } from '../services';

import DetailsSidebarHeader from './DetailsSidebar/DetailsSidebarHeader/DetailsSidebarHeader';
import EdcVisitDatePickerButton from './EdcVisitDatePickerButton/EdcVisitDatePickerButton';
import InformedConsentContextProvider, { InformedConsentContext } from './InformedConsent/InformedConsentContext';
import { OutreachAttemptTrackingModal } from './OutreachAttemptTrackingModal/OutreachAttemptTrackingModal';
import ReviewsSidebarBlock from './ReviewsSidebarBlock/ReviewsSidebarBlock';
import StudyManagerSelector from './StudyManagerSelector/StudyManagerSelector';
import NewEncounterPageContext, { EncounterPageContext } from './NewEncounterPageContext';
import NewEncounterPageInfo from './NewEncounterPageInfo';
import {
  getListOfStudyManagers,
  getRegularEncounterDetails,
  getRouteKey,
  loadEncounterDetailsBoxes,
  saveEdcVisitDate
} from './NewEncounterPageService';
import { onUpdateEventEmitterService } from './onUpdateEventEmmiterService';

import './NewEncounterPage.scss';

function NewEncounterPage() {
  const route = useCurrentRoute();
  const navigate = useNavigate();
  const { patientId, patientEncounterId, ssuPatientId, patientItemGroupId } = route.params;
  const [searchString, setSearchString] = useState(null);
  const [encounterStudyManagers, setEncounterStudyManagers] = useState([]);
  const [selectedStudyManager, setSelectedStudyManager] = useState(null);
  const [edcVisitDate, setEdcVisitDate] = useState(null);
  const currentRoute = useCurrentRoute();
  const [logRowStatus, setLogRowStatus] = useState(null);

  const [storedItemGroup, setStoredItemGroup] = useSessionStorage('NEW_ENCOUNTER_PAGE_IG', {});

  const EncounterContext = useContext(EncounterPageContext);
  const IcfContext = useContext(InformedConsentContext);

  const {
    currentEncounterIsClosable,
    metaData,
    selectedItemGroup,
    setSelectedItemGroup,
    encounterDetails,
    state,
    setState,
    loadUpdatedData
  } = EncounterContext;

  const { allIcfForms, completedIcfForms } = IcfContext;

  const generateUrlForItem = useCallback(
    function(key, item) {
      return generateUrlByKey(getRouteKey(key, item), {
        patientId,
        ssuPatientId,
        patientEncounterId,
        patientItemGroupId: item.formUniqueIdentifier,
        typeId: item?.informedConsentTemplate?.typeId,
        languageId: item?.informedConsentTemplate?.languageId
      });
    },
    [patientEncounterId, patientId, ssuPatientId]
  );

  const selectNextItemGroup = useCallback(
    function(encounterDetailsList) {
      //TODO: improve this logic after clarifying all business cases
      if (
        encounterDetailsList &&
        selectedItemGroup &&
        selectedItemGroup.domainCode !== 'CO' &&
        selectedItemGroup.elementType !== 'Logs' &&
        selectedItemGroup.domainCode !== 'ICF' &&
        selectedItemGroup.elementType !== 'Table' &&
        selectedItemGroup.elementType !== 'ICF'
      ) {
        const nextItemGroup = getNextItemGroup(encounterDetailsList, selectedItemGroup);

        if (!isEmpty(nextItemGroup)) {
          setSelectedItemGroup(nextItemGroup);
          navigate(generateUrlForItem(currentRoute.key, nextItemGroup));
        } else {
          navigate(currentRoute.pathname);
        }
        return;
      }

      if (selectedItemGroup.elementType === 'Logs') {
        navigate(generateUrlForItem(currentRoute.key, selectedItemGroup), { replace: true });
      }
    },
    [currentRoute.key, currentRoute.pathname, generateUrlForItem, navigate, selectedItemGroup, setSelectedItemGroup]
  );

  useEffect(() => {
    const { rowId, ssuPatientId, itemGroupTemplateVersionId } = currentRoute.params;
    if (
      (userHasRole(ROLE_STUDY_MANAGER) ||
        userHasRole(ROLE_PRINCIPAL_INVESTIGATOR) ||
        userHasRole(ROLE_SYSTEM_ADMINISTRATOR)) &&
      rowId &&
      rowId !== 'newRow' &&
      selectedItemGroup &&
      isReviewableLogType(selectedItemGroup.domainCode, selectedItemGroup.elementType)
    ) {
      LogRowStatusHistoryApi.getLogRowLastStatus(
        metaData?.studyId,
        ssuPatientId,
        selectedItemGroup?.itemGroupTemplateId,
        itemGroupTemplateVersionId,
        rowId
      ).then(({ data }) => {
        setLogRowStatus(data);
      });
    }
  }, [currentRoute, selectedItemGroup, selectedItemGroup?.itemGroupTemplateId, metaData?.studyId]);

  useEffect(
    function() {
      return onUpdateEventEmitterService(function(onUpdate) {
        loadUpdatedData().then(
          encounterDetailsList => onUpdate.selectNext && selectNextItemGroup(encounterDetailsList)
        );
      });
    },
    [loadUpdatedData, selectNextItemGroup]
  );

  let proceduresAndLogs = [];
  let informedConsent = [];
  let groupAssignReviews = [];

  useEffect(function() {
    async function load() {
      const encounter = await getRegularEncounterDetails(patientEncounterId, ssuPatientId);
      const encounterDetailsBoxes = await loadEncounterDetailsBoxes(patientEncounterId, ssuPatientId);
      const encounterStudyManagers = await getListOfStudyManagers(ssuPatientId);
      setEncounterStudyManagers(encounterStudyManagers);

      setState({ encounter, ...encounterDetailsBoxes });

      const sm = encounterStudyManagers.find(e => e.uniqueIdentifier === encounter.studyManagerUniqueIdentifier);

      setEdcVisitDate(encounter.edcVisitDate);
      sm && setSelectedStudyManager(sm);

      let itemGroupForSelect;
      if (
        !patientItemGroupId &&
        !isEmpty(storedItemGroup) &&
        storedItemGroup.patientEncounterId === patientEncounterId
      ) {
        itemGroupForSelect = encounterDetailsBoxes.encounterDetailsList.find(
          e => e.formUniqueIdentifier === storedItemGroup.patientItemGroupId && e.permissionType !== DENY_ALL
        );
      }
      if (!patientItemGroupId && !itemGroupForSelect) {
        const notSkippedIcf = encounterDetailsBoxes.encounterDetailsList.filter(
          e => e.skipped === 0 && e.elementType === 'ICF' && e.permissionType !== DENY_ALL
        );

        const notSkippedProceduresAndLogs = encounterDetailsBoxes.encounterDetailsList.filter(
          e => e.skipped === 0 && e.permissionType !== DENY_ALL
        );

        if (!isEmpty(notSkippedIcf)) {
          itemGroupForSelect = notSkippedIcf[0];
        } else if (!isEmpty(notSkippedProceduresAndLogs)) {
          itemGroupForSelect = notSkippedProceduresAndLogs[0];
        }
      }
      if (!isEmpty(itemGroupForSelect)) {
        navigate(generateUrlForItem(currentRoute.key, itemGroupForSelect), { replace: true });
      }
    }
    load().then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    function() {
      Promise.all([
        getRegularEncounterDetails(patientEncounterId, ssuPatientId),
        loadEncounterDetailsBoxes(patientEncounterId, ssuPatientId)
      ]).then(([encounter, encounterDetailsBoxes]) => {
        setState({ encounter, ...encounterDetailsBoxes });
      });
    },
    [patientEncounterId, ssuPatientId, patientItemGroupId, setState]
  );

  useEffect(
    function() {
      if (patientItemGroupId && state?.encounterDetailsList) {
        let selected = state.encounterDetailsList.find(
          e => e.formUniqueIdentifier === patientItemGroupId && e.permissionType !== DENY_ALL
        );
        if (isEmpty(selected)) {
          selected = state.encounterDetailsList.find(e => e.domainCode === 'CO');
          navigate(generateUrlForItem(currentRoute.key, selected));
        } else {
          setSelectedItemGroup(selected);
          setStoredItemGroup({
            patientEncounterId,
            patientItemGroupId: selected.formUniqueIdentifier
          });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state?.encounterDetailsList, patientItemGroupId, setStoredItemGroup, patientEncounterId]
  );

  useEffect(
    function() {
      if (currentRoute.name === 'Encounter Details') {
        let selected;
        if (selectedItemGroup) {
          selected = selectedItemGroup;
        } else if (state?.encounterDetailsList) {
          const notSkipped = state.encounterDetailsList.filter(e => e.skipped === 0 && e.permissionType !== DENY_ALL);
          if (!isEmpty(notSkipped)) {
            selected = notSkipped[0];
          }
        }

        if (selected) {
          navigate(generateUrlForItem(currentRoute.key, selected));
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentRoute]
  );

  if (state?.encounterDetailsList) {
    let filtered = state.encounterDetailsList;
    let filteredReviews = encounterDetails?.reviews;
    if (!isEmpty(searchString)) {
      filtered = state.encounterDetailsList.filter(e =>
        e.elementName.toLowerCase().includes(searchString.toLowerCase())
      );
      filteredReviews = filteredReviews?.filter(review =>
        review.reviewName.toLowerCase().includes(searchString.toLowerCase())
      );
    }
    proceduresAndLogs = filtered.filter(e => !['ICF'].includes(e.elementType));
    informedConsent = filtered.filter(e => e.elementType === 'ICF');
    groupAssignReviews = filteredReviews;
  }

  function selectItemGroup(itemGroup) {
    setSelectedItemGroup(null);
    setTimeout(() => {
      setSelectedItemGroup(itemGroup);
    });
  }

  function Item(item) {
    let isNewIcfForm = false;
    let activeInformedConsentForEncounter = null;
    if (item && item.domainCode === 'ICF') {
      const typeId = item?.informedConsentTemplate?.typeId;
      const languageId = item?.informedConsentTemplate?.languageId;
      const forms = allIcfForms.filter(e => e.typeId === typeId && e.languageId === languageId);

      if (forms) {
        const form = maxBy(forms, a => moment(a.uploadDate).format('x'));
        if (form) {
          activeInformedConsentForEncounter = form;
        }

        const activeInformedConsentForm = forms?.find(form => form?.active);
        const activeFormHasAnswer = completedIcfForms?.some(
          e => e?.informedConsentFormId === activeInformedConsentForm?.id
        );
        isNewIcfForm =
          item.completePercentage === '0.0' && item.skipped === 0 && activeInformedConsentForm && !activeFormHasAnswer;
      }
    }

    return (
      <ItemGroup
        unexpectedEncounter={false}
        selectedItemGroups={state?.encounterDetailsList}
        key={item.uniqueIdentifier}
        encounter={item}
        patientId={metaData?.patientEncounterGroupId}
        loadEncounterDetailData={performed =>
          loadUpdatedData().then(encounterDetailsList => {
            if (selectedItemGroup === item) {
              if (!performed) {
                selectNextItemGroup(encounterDetailsList);
              } else {
                navigate(generateUrlForItem(currentRoute.key, item));
              }
            } else {
              if (performed) {
                navigate(generateUrlForItem(currentRoute.key, item));
              }
            }
          })
        }
        unExpectedEncounterData={{
          encounterDetailsList: state.encounterDetailsList,
          isUnexpected: metaData?.encounterType === UNEXPECTED
        }}
        disabled={state.encounter.encounterStatus === 'Canceled'}
        studyId={metaData?.studyId}
        isSelected={selectedItemGroup === item}
        isNewIcfForm={isNewIcfForm}
        informedConsentForm={activeInformedConsentForEncounter}
      />
    );
  }

  const isAbleToManageEncounterData = () => {
    return isAbleToEditEncounter(state.encounter?.nonProtocol, state.encounter?.ssuStatus);
  };

  const markItemGroupsNotPerformedAndNotAsked = () => {
    const notPerformedModal = ModalBoxes.open({
      component: (
        <NotPerformShortcutModal
          onConfirm={reason => {
            notPerformedAndNotAskedShortcut(
              state?.encounter?.nonProtocol,
              state.encounterDetailsList,
              metaData.studyId,
              ssuPatientId,
              patientEncounterId,
              reason,
              state.encounter.studyManagerUniqueIdentifier
            ).then(() => {
              notPerformedModal.close();
              loadUpdatedData().then();
            });
          }}
          notStarted={getNotStartedEncounterNames(state.encounterDetailsList).sort()}
          partial={getStartedEncounterNames(state.encounterDetailsList).sort()}
        />
      )
    });
  };

  const cancelEncounter = () => {
    cancelSitePatientEncounter(
      patientEncounterId,
      ssuPatientId,
      state.encounter.encounterIdentifier,
      () => {
        loadUpdatedData().then(() => {
          navigate(generateUrlForItem(currentRoute.key, selectedItemGroup), { replace: true });
        });
      },
      state.encounter.encounterName
    );
  };

  const reopenEncounter = () => {
    reopenSitePatientEncounter(ssuPatientId, patientEncounterId, loadUpdatedData);
  };
  const onPatientPrimaryContactUpdate = ({ primaryPhoneCode, contactNumber }) => {
    const { currentPatient } = state;
    setState({ ...state, currentPatient: { ...currentPatient, primaryPhoneCode, contactNumber } });
  };

  const onAttemptTracking = () => {
    ModalBoxes.open({
      component: (
        <OutreachAttemptTrackingModal
          ssuPatientId={ssuPatientId}
          viewOnly={!userHasAccessTo(TD_MANAGE_ANY_ENCOUNTER_DATA) || selectedItemGroup?.permissionType === ALLOW_READ}
        />
      ),
      title: 'Contact Attempt Tracking',
      size: 'w1100',
      className: 'outreach-attempt-tracking-modal'
    });
  };

  function resolveStatus() {
    if (userHasAccessAs([ROLE_STUDY_MANAGER, ROLE_PRINCIPAL_INVESTIGATOR])) {
      return resolveSmStatus(logRowStatus?.smStatus, isNull(logRowStatus?.piStatus));
    }
  }

  return (
    <div className={'new-encounter-page'}>
      {state.currentPatient && (
        <NewEncounterPageInfo
          currentPatient={state.currentPatient}
          showPrimaryContact={
            ['EDO', 'ECR'].includes(get(selectedItemGroup, 'domainCode')) &&
            userHasAccessAs([ROLE_STUDY_MANAGER, ROLE_PATIENT_REPRESENTATIVE])
          }
          onPatientPrimaryContactUpdate={onPatientPrimaryContactUpdate}
          showAttemptTracking={get(selectedItemGroup, 'domainCode') === 'EDO'}
          onAttemptTracking={onAttemptTracking}
        />
      )}
      {state.encounter && (
        <div className="content">
          <div className="left">
            <DetailsSidebarHeader
              encounter={state.encounter}
              currentPatient={state.currentPatient}
              encounterDetailsList={state?.encounterDetailsList}
              encounterType={metaData?.encounterType}
              currentEncounterIsClosable={currentEncounterIsClosable}
              encounterStatus={encounterDetails?.metaInfo?.encounterStatus}
              cancelEncounter={cancelEncounter}
              reopenEncounter={reopenEncounter}
              markItemGroupsNotPerformedAndNotAsked={markItemGroupsNotPerformedAndNotAsked}
              setSearchString={setSearchString}
            />
            <div className={'left-content'}>
              <div>
                {!isEmpty(informedConsent) && (
                  <>
                    <h5 className={'pl-3 mt-4'}>Informed Consents</h5>
                    {informedConsent.map(e => Item(e, selectItemGroup, selectedItemGroup))}
                  </>
                )}
                {!isEmpty(proceduresAndLogs) && (
                  <>
                    <h5 className={'pl-3 mt-4'}>Procedures and Assessments</h5>
                    {proceduresAndLogs.map(e => Item(e, selectItemGroup, selectedItemGroup))}
                  </>
                )}
                <ReviewsSidebarBlock
                  loading={isEmpty(encounterDetails)}
                  reviews={groupAssignReviews}
                  userGroupAssignId={encounterDetails.userGroupAssignId}
                />
              </div>
              <div className="left-footer">
                <div className="my-3">
                  <StudyManagerSelector
                    value={selectedStudyManager}
                    encounterStudyManagers={encounterStudyManagers}
                    onChange={e =>
                      saveStudyManagerForEncounter(ssuPatientId, patientEncounterId, e.uniqueIdentifier).then(() => {
                        setSelectedStudyManager(e);
                      })
                    }
                  />
                </div>
                <div>
                  <EdcVisitDatePickerButton
                    isAllowedToManage={isAbleToManageEncounterData}
                    onChange={date => {
                      saveEdcVisitDate(ssuPatientId, patientEncounterId, date).then(res => {
                        setEdcVisitDate(res);
                      });
                    }}
                    value={moment(edcVisitDate, 'YYYY-MM-DD')}
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            className={cx('right pl-2', {
              disabled: state.encounter.encounterStatus === 'Canceled',
              'read-only': selectedItemGroup?.permissionType === ALLOW_READ
            })}
          >
            {selectedItemGroup && (
              <ItemGroupBar
                className="pt-3 px-3"
                name={
                  <>
                    {selectedItemGroup.elementName}
                    {selectedItemGroup.skipped
                      ? ` - Not ${selectedItemGroup.elementType === 'ICF' ? 'Applicable' : 'Performed'}`
                      : ''}
                  </>
                }
                statusChip={
                  isNotNewReviewableLogType(
                    currentRoute.params.rowId,
                    selectedItemGroup.domainCode,
                    selectedItemGroup.elementType
                  ) && resolveStatus()
                }
              >
                {isNotNewReviewableLogType(
                  currentRoute.params.rowId,
                  selectedItemGroup.domainCode,
                  selectedItemGroup.elementType
                ) && (
                  <LogRowCloseReopen
                    logRowStatus={logRowStatus}
                    studyId={metaData?.studyId}
                    itemGroupTemplateId={selectedItemGroup.itemGroupTemplateId}
                    elementName={selectedItemGroup.elementName}
                    currentDomainCode={selectedItemGroup.domainCode}
                  />
                )}
              </ItemGroupBar>
            )}
            {state.encounter.encounterStatus === 'Canceled' && <div className={'disabled-cover'} />}
            {selectedItemGroup && selectedItemGroup.permissionType !== DENY_ALL && <Outlet />}
          </div>
        </div>
      )}
    </div>
  );
}

function getNextItemGroup(itemGroups, currentItemGroup) {
  const notSkipped = itemGroups.filter(function(itemGroup) {
    return (
      itemGroup.skipped === 0 &&
      itemGroup.permissionType !== DENY_ALL &&
      itemGroup.completePercentage !== '100.0' &&
      itemGroup.domainCode !== 'CO'
    );
  });

  if (isEmpty(notSkipped)) {
    return null;
  }

  const notSkippedAndNotIcf = notSkipped.filter(function(itemGroup) {
    return itemGroup.domainCode !== 'ICF' && itemGroup.elementType !== 'ICF';
  });

  const firstNotSkippedIcf = notSkipped.find(function(itemGroup) {
    return itemGroup.domainCode === 'ICF' || itemGroup.elementType === 'ICF';
  });

  const nextBySequence = notSkippedAndNotIcf.find(function(itemGroup) {
    return itemGroup.itemGroupSequence > currentItemGroup.itemGroupSequence;
  });

  if (nextBySequence) {
    return nextBySequence;
  }

  if (firstNotSkippedIcf) {
    return firstNotSkippedIcf;
  }

  return notSkippedAndNotIcf[0];
}

// eslint-disable-next-line import/no-anonymous-default-export
export default function(props) {
  return (
    <NewEncounterPageContext>
      <InformedConsentContextProvider>
        <NewEncounterPage {...props} />
      </InformedConsentContextProvider>
    </NewEncounterPageContext>
  );
}
